import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaEdit, FaTrash, FaPlus, FaArrowLeft, FaFilePdf, FaUpload, FaEye, FaSpinner } from 'react-icons/fa';
import projectService from '../components/services/projectService';

const getImageUrl = (imagePath) => {
  if (!imagePath) return '/placeholder.jpg';
  if (imagePath.startsWith('https')) return imagePath;
  if (imagePath.startsWith('/uploads')) {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://alafoogarage.com/ahalafoo';
    return `${baseUrl}${imagePath}`;
  }
  return `${process.env.REACT_APP_API_URL || 'https://alafoogarage.com/ahalafoo'}${imagePath}`;
};

const AdminInterface = ({ isOpen, onClose, onUpdateProjects }) => {
  const [activeTab, setActiveTab] = useState('live');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [adminVerified, setAdminVerified] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [projectsData, setProjectsData] = useState({ live: [], completed: [] });
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  // Profile Management States
  const [profileManagementOpen, setProfileManagementOpen] = useState(false);
  const [profileExists, setProfileExists] = useState(false);
  const [profileFile, setProfileFile] = useState(null);
  const [profileUploading, setProfileUploading] = useState(false);

  // Certificate Management States
  const [certificateManagementOpen, setCertificateManagementOpen] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [certificateFile, setCertificateFile] = useState(null);
  const [selectedCertificate, setSelectedCertificate] = useState('');
  const [certificateUploading, setCertificateUploading] = useState(false);

  useEffect(() => {
    if (isOpen && adminVerified) {
      fetchProjects();
      checkProfileExists();
      fetchCertificates();
    }
  }, [isOpen, adminVerified, activeTab]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService.getProjects();
      setProjectsData(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkProfileExists = async () => {
    try {
      const response = await fetch('https://alafoogarage.com/ahalafoo/api/profile');
      setProfileExists(response.ok);
    } catch (error) {
      setProfileExists(false);
    }
  };

  const fetchCertificates = async () => {
    try {
      const response = await fetch('https://alafoogarage.com/ahalafoo/api/certificates');
      const data = await response.json();
      if (data.success) {
        setCertificates(data.certificates);
      }
    } catch (error) {
      console.error('Error fetching certificates:', error);
    }
  };

  const handleProfileUpload = async () => {
    if (!profileFile) return;
    setProfileUploading(true);
    try {
      const formData = new FormData();
      formData.append('profile', profileFile);

      const response = await fetch('https://alafoogarage.com/ahalafoo/api/profile', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        alert('Profile uploaded successfully');
        setProfileFile(null);
        setProfileExists(true);
        setProfileManagementOpen(false);
      } else {
        alert('Error uploading profile: ' + data.message);
      }
    } catch (error) {
      alert('Error uploading profile: ' + error.message);
    } finally {
      setProfileUploading(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (window.confirm('Are you sure you want to delete the company profile?')) {
      try {
        const response = await fetch('https://alafoogarage.com/ahalafoo/api/profile', {
          method: 'DELETE',
        });

        const data = await response.json();

        if (data.success) {
          alert('Profile deleted successfully');
          setProfileExists(false);
          setProfileManagementOpen(false);
        } else {
          alert('Error deleting profile: ' + data.message);
        }
      } catch (error) {
        alert('Error deleting profile: ' + error.message);
      }
    }
  };

  const handleCertificateUpload = async () => {
    if (!certificateFile || !selectedCertificate) return;
    setCertificateUploading(true);
    try {
      const formData = new FormData();
      formData.append('certificate', certificateFile);
      formData.append('name', selectedCertificate);

      const response = await fetch('https://alafoogarage.com/ahalafoo/api/certificates', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        alert('Certificate uploaded successfully');
        setCertificateFile(null);
        setSelectedCertificate('');
        fetchCertificates();
      } else {
        alert('Error uploading certificate: ' + data.message);
      }
    } catch (error) {
      alert('Error uploading certificate: ' + error.message);
    } finally {
      setCertificateUploading(false);
    }
  };

  const handleDeleteCertificate = async (filename) => {
    if (window.confirm('Are you sure you want to delete this certificate?')) {
      try {
        const response = await fetch(`https://alafoogarage.com/ahalafoo/api/certificates/${filename}`, {
          method: 'DELETE',
        });

        const data = await response.json();

        if (data.success) {
          alert('Certificate deleted successfully');
          fetchCertificates();
        } else {
          alert('Error deleting certificate: ' + data.message);
        }
      } catch (error) {
        alert('Error deleting certificate: ' + error.message);
      }
    }
  };

  const sendOtp = async () => {
    try {
      setSendingOtp(true);
      setLoginError('');
      const adminEmail = 'm.thawfeeq@gmail.com';
      const response = await fetch(`https://alafoogarage.com/ahalafoo/api/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: adminEmail }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpSent(true);
        setLoginError('');
      } else {
        setLoginError(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      setLoginError('Failed to send OTP. Please try again.');
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    try {
      setVerifyingOtp(true);
      setLoginError('');
      const adminEmail = 'm.thawfeeq@gmail.com';
      const response = await fetch(`https://alafoogarage.com/ahalafoo/api/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: adminEmail, otp }),
      });
      const data = await response.json();
      if (data.success) {
        setAdminVerified(true);
        setLoginError('');
      } else {
        setLoginError(data.message || 'Invalid OTP');
      }
    } catch (error) {
      setLoginError('Failed to verify OTP. Please try again.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleSaveProject = async (formData) => {
    try {
      let result;
      if (formData.get('_id')) {
        result = await projectService.updateProject(formData.get('_id'), formData);
      } else {
        result = await projectService.createProject(formData);
      }
      await fetchProjects();
      if (onUpdateProjects) onUpdateProjects();
      setSelectedProject(null);
      setIsEditing(false);
    } catch (error) {
      alert('Error saving project: ' + error.message);
    }
  };

  const handleAddProject = () => {
    setSelectedProject({
      projectName: '',
      content: '',
      photos: [],
      contractValue: 0,
      mainPhoto: '',
      completed: activeTab === 'completed',
      progress: activeTab === 'completed' ? 100 : 0,
      category: 'Road Infrastructure',
      startDate: new Date().toISOString().split('T')[0],
      client: ''
    });
    setIsEditing(true);
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await projectService.deleteProject(projectId);
        await fetchProjects();
        if (onUpdateProjects) onUpdateProjects();
      } catch (error) {
        alert('Error deleting project: ' + error.message);
      }
    }
  };

  const handleDeletePhoto = async (projectId, photoIndex) => {
    try {
      await projectService.deletePhoto(projectId, photoIndex);

      // Update local editor state immediately
      if (selectedProject && selectedProject._id === projectId) {
        const updatedPhotos = selectedProject.photos.filter((_, i) => i !== photoIndex);
        setSelectedProject(prev => ({ ...prev, photos: updatedPhotos }));
      }

      await fetchProjects();
      if (onUpdateProjects) onUpdateProjects();
    } catch (error) {
      alert('Error deleting photo: ' + error.message);
    }
  };


  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="admin-overlay"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="admin-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="admin-close-btn" onClick={onClose}><FaTimes /></button>

          {!adminVerified ? (
            <div className="admin-login-panel">
              <h2>Admin Access</h2>
              {!otpSent ? (
                <>
                  <p>Click the button below to receive an OTP via email</p>
                  <button
                    onClick={sendOtp}
                    className="admin-btn admin-btn-primary"
                    disabled={sendingOtp}
                  >
                    {sendingOtp ? (
                      <>
                        <FaSpinner className="spinner" />
                        Sending OTP...
                      </>
                    ) : (
                      'Send OTP'
                    )}
                  </button>
                </>
              ) : (
                <>
                  <p>Enter the OTP sent to m.thawfeeq@gmail.com</p>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter OTP"
                    maxLength={6}
                    className="admin-otp-input"
                  />
                  <button
                    onClick={verifyOtp}
                    className="admin-btn admin-btn-primary"
                    disabled={verifyingOtp || otp.length !== 6}
                  >
                    {verifyingOtp ? (
                      <>
                        <FaSpinner className="spinner" />
                        Verifying...
                      </>
                    ) : (
                      'Verify OTP'
                    )}
                  </button>
                  <button
                    onClick={() => setOtpSent(false)}
                    className="admin-btn admin-btn-secondary"
                    disabled={verifyingOtp}
                  >
                    Back
                  </button>
                </>
              )}
              {loginError && <p className="admin-error-msg">{loginError}</p>}
            </div>
          ) : (
            <div className="admin-panel-container">
              <div className="admin-panel-header">
                <h2>Project Management</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setCertificateManagementOpen(true)}
                    className="admin-btn admin-btn-secondary"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <FaFilePdf /> Manage Certificates
                  </button>
                  <button
                    onClick={() => setProfileManagementOpen(true)}
                    className="admin-btn admin-btn-secondary"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <FaFilePdf /> Manage Profile
                  </button>
                  <button onClick={() => setAdminVerified(false)} className="admin-btn admin-btn-secondary">
                    <FaArrowLeft /> Logout
                  </button>
                </div>
              </div>

              <div className="admin-tabs-container">
                <button
                  className={`admin-tab-btn ${activeTab === 'live' ? 'admin-tab-active' : ''}`}
                  onClick={() => setActiveTab('live')}
                >
                  Live Projects
                </button>
                <button
                  className={`admin-tab-btn ${activeTab === 'completed' ? 'admin-tab-active' : ''}`}
                  onClick={() => setActiveTab('completed')}
                >
                  Completed Projects
                </button>
              </div>

              <button className="admin-btn admin-btn-primary admin-add-project-btn" onClick={handleAddProject}>
                <FaPlus /> Add New Project
              </button>

              {loading ? (
                <div className="admin-loading">
                  <FaSpinner className="spinner" /> Loading projects...
                </div>
              ) : (
                <div className="admin-projects-list">
                  {projectsData[activeTab]?.length === 0 ? (
                    <div className="admin-no-projects">
                      <p>No {activeTab} projects available.</p>
                    </div>
                  ) : (
                    projectsData[activeTab]?.map(project => {
                      const mainPhotoUrl = getImageUrl(project.mainPhoto || (project.photos && project.photos[0]));
                      return (
                        <div key={project._id} className="admin-project-card">
                          <img src={mainPhotoUrl} alt={project.projectName} onError={(e) => { e.target.src = '/placeholder.jpg' }} className="admin-project-img" />
                          <div className="admin-project-info">
                            <h3>{project.projectName}</h3>
                            <p>{project.category}</p>
                          </div>
                          <div className="admin-project-actions">
                            <button
                              onClick={() => { setSelectedProject(project); setIsEditing(true); }}
                              className="admin-action-btn admin-edit-btn"
                            ><FaEdit /></button>
                            <button
                              onClick={() => handleDeleteProject(project._id)}
                              className="admin-action-btn admin-delete-btn"
                            ><FaTrash /></button>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              )}

              {isEditing && (
                <ProjectEditor
                  project={selectedProject}
                  onSave={handleSaveProject}
                  onCancel={() => { setSelectedProject(null); setIsEditing(false); }}
                  onDeletePhoto={handleDeletePhoto}
                  isNew={!selectedProject._id}
                  projectType={activeTab}
                />
              )}

              {/* Certificate Management Modal */}
              {certificateManagementOpen && (
                <div className="admin-modal-overlay">
                  <div className="admin-modal-content">
                    <h3>Certificate Management</h3>

                    <div className="certificate-upload-section">
                      <h4>Upload/Replace Certificate</h4>
                      <div className="certificate-form-group">
                        <label>Select Certificate</label>
                        <select
                          value={selectedCertificate}
                          onChange={(e) => setSelectedCertificate(e.target.value)}
                          className="admin-form-select"
                        >
                          <option value="">Select a certificate</option>
                          {certificates.map((certificate, index) => (
                            <option
                              key={index}
                              value={certificate.filename}
                            >
                              {certificate.name} {certificate.exists ? '(Already Uploaded)' : '(Missing)'}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedCertificate && (
                        <>
                          <div className="certificate-form-group">
                            <label>Certificate File (PDF)</label>
                            <input
                              type="file"
                              accept=".pdf"
                              onChange={(e) => setCertificateFile(e.target.files[0])}
                              className="admin-file-input"
                            />
                          </div>
                          {certificateFile && (
                            <p>Selected file: {certificateFile.name}</p>
                          )}
                          <button
                            onClick={handleCertificateUpload}
                            disabled={!certificateFile || !selectedCertificate || certificateUploading}
                            className="admin-btn admin-btn-primary"
                            style={{ marginTop: '10px' }}
                          >
                            {certificateUploading ? (
                              <>
                                <FaSpinner className="spinner" /> Uploading...
                              </>
                            ) : 'Upload Certificate'}
                          </button>
                        </>
                      )}
                    </div>

                    <div className="certificates-list">
                      <h4>Certificate Status</h4>
                      {certificates.length === 0 ? (
                        <p>No certificates available.</p>
                      ) : (
                        <div className="certificates-grid-admin">
                          {certificates.map((certificate, index) => (
                            <div key={index} className="certificate-item-admin">
                              <div className="certificate-info">
                                <h5>{certificate.name}</h5>
                                <p>Status: {certificate.exists ? 'Uploaded' : 'Missing'}</p>
                              </div>
                              {certificate.exists ? (
                                <>
                                  <a
                                    href={`https://alafoogarage.com/ahalafoo/uploads/certificates/${certificate.filename}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="admin-btn admin-btn-primary"
                                    style={{ marginRight: '10px' }}
                                  >
                                    <FaEye /> View
                                  </a>
                                  <button
                                    onClick={() => handleDeleteCertificate(certificate.filename)}
                                    className="admin-btn admin-btn-danger"
                                  >
                                    <FaTrash /> Delete
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => setSelectedCertificate(certificate.filename)}
                                  className="admin-btn admin-btn-secondary"
                                >
                                  Select to Upload
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => {
                        setCertificateManagementOpen(false);
                        setSelectedCertificate('');
                        setCertificateFile(null);
                      }}
                      className="admin-btn admin-btn-secondary"
                      style={{ marginTop: '20px' }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

              {/* Profile Management Modal */}
              {profileManagementOpen && (
                <div className="admin-modal-overlay">
                  <div className="admin-modal-content">
                    <h3>Company Profile Management</h3>

                    {profileExists ? (
                      <div className="profile-status">
                        <p>Company profile is currently available.</p>
                        <div className="profile-actions">
                          <a
                            href="https://alafoogarage.com/ahalafoo/api/profile"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="admin-btn admin-btn-primary"
                          >
                            <FaEye /> View Profile
                          </a>
                          <button
                            onClick={handleDeleteProfile}
                            className="admin-btn admin-btn-danger"
                          >
                            <FaTrash /> Delete Profile
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="profile-upload">
                        <p>No company profile available. Upload a new PDF profile.</p>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setProfileFile(e.target.files[0])}
                          className="profile-file-input"
                        />
                        {profileFile && (
                          <p>Selected file: {profileFile.name}</p>
                        )}
                        <div className="profile-upload-actions">
                          <button
                            onClick={handleProfileUpload}
                            disabled={!profileFile || profileUploading}
                            className="admin-btn admin-btn-primary"
                          >
                            {profileUploading ? (
                              <>
                                <FaSpinner className="spinner" /> Uploading...
                              </>
                            ) : (
                              <>
                                <FaUpload /> Upload Profile
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => setProfileManagementOpen(false)}
                      className="admin-btn admin-btn-secondary"
                      style={{ marginTop: '20px' }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const ProjectEditor = ({ project, onSave, onCancel, onDeletePhoto, isNew, projectType }) => {
  const [editedProject, setEditedProject] = useState({
    ...project,
    completed: project?.completed === true || project?.completed === 'true',
    progress: project?.progress || 0
  });
  const [newPhotos, setNewPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setEditedProject({
      ...project,
      completed: project?.completed === true || project?.completed === 'true',
      progress: project?.progress || 0
    });
    setNewPhotos([]);
  }, [project?._id]);
  useEffect(() => {
    if (project) {
      setEditedProject(project);
    }
  }, [project]);


  const handleInputChange = (field, value) => {
    if (field === "progress") {
      const numericValue = parseInt(value, 10);
      const completed = numericValue === 100;

      setEditedProject(prev => ({
        ...prev,
        progress: numericValue,
        completed: completed
      }));
    } else {
      setEditedProject(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleCheckboxChange = (field, checked) => {
    if (field === "completed") {
      const progress = checked ? 100 : (editedProject.progress === 100 ? 0 : editedProject.progress);

      setEditedProject(prev => ({
        ...prev,
        completed: checked,
        progress: progress
      }));
    } else {
      setEditedProject(prev => ({ ...prev, [field]: checked }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const existingCount = editedProject.photos ? editedProject.photos.length : 0;
    const availableSlots = 6 - existingCount - newPhotos.length;

    if (availableSlots <= 0) {
      alert('Maximum 6 photos allowed. Delete some existing photos first.');
      e.target.value = '';
      return;
    }

    const limitedFiles = files.slice(0, availableSlots);
    setNewPhotos(prev => [...prev, ...limitedFiles]);

    if (files.length > availableSlots) {
      alert(`Only ${availableSlots} more photos can be added. The rest were ignored.`);
    }
  };

  const handleRemoveNewPhoto = (index) => {
    setNewPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const formData = new FormData();
      Object.keys(editedProject).forEach(key => {
        if (key !== 'photos' && key !== '_id') {
          formData.append(key, key === 'completed' ? (editedProject[key] ? 'true' : 'false') : editedProject[key] || '');
        }
      });
      if (editedProject._id) formData.append('_id', editedProject._id);
      newPhotos.forEach(photo => formData.append('photos', photo));
      await onSave(formData);
    } catch (error) {
      alert('Error saving project: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const existingCount = editedProject.photos ? editedProject.photos.length : 0;
  const newCount = newPhotos.length;
  const totalCount = existingCount + newCount;
  const canAddMore = totalCount < 6;

  return (
    <div className="project-editor-overlay">
      <div className="project-editor-modal">
        <h3>{isNew ? 'Add New Project' : 'Edit Project'}</h3>
        <form onSubmit={handleSubmit} className="project-editor-form">
          {/* Project Name */}
          <div className="project-form-group">
            <label>Project Name *</label>
            <input type="text" value={editedProject.projectName || ''} onChange={e => handleInputChange('projectName', e.target.value)} required className="project-form-input" />
          </div>
          {/* Category */}
          <div className="project-form-group">
            <label>Category *</label>
            <select value={editedProject.category || 'Road Infrastructure'} onChange={e => handleInputChange('category', e.target.value)} required className="project-form-select">
              <option value="Road Infrastructure">Road Infrastructure</option>
              <option value="Sewerage & Drainage">Sewerage & Drainage</option>
              <option value="Electrical Infrastructure">Electrical Infrastructure</option>
              <option value="Water Distribution">Water Distribution</option>
              <option value="Civil Works">Civil Works</option>
              <option value="Other">Other</option>
            </select>
          </div>
          {/* Description */}
          <div className="project-form-group">
            <label>Project Description *</label>
            <textarea value={editedProject.content || ''} onChange={e => handleInputChange('content', e.target.value)} required rows={3} className="project-form-textarea" />
          </div>
          {/* Contract Value */}
          <div className="project-form-group">
            <label>Contract Value (BD) *</label>
            <input type="number" value={editedProject.contractValue || 0} onChange={e => handleInputChange('contractValue', e.target.value)} required min="0" step="0.01" className="project-form-input" />
          </div>
          {/* Progress */}
          <div className="project-form-group">
            <label>Progress (%)</label>
            <div className="progress-slider-container">
              <input
                type="range"
                min="0"
                max="100"
                value={editedProject.progress || 0}
                onChange={e => handleInputChange('progress', e.target.value)}
                className="progress-slider"
              />
              <span className="progress-value">{editedProject.progress || 0}%</span>
            </div>
          </div>
          {/* Start Date */}
          <div className="project-form-group">
            <label>Start Date</label>
            <input type="date" value={editedProject.startDate || ''} onChange={e => handleInputChange('startDate', e.target.value)} className="project-form-input" />
          </div>
          {/* Conditional Completed / Expected */}
          {editedProject.completed ? (
            <>
              <div className="project-form-group">
                <label>Completion Date</label>
                <input type="date" value={editedProject.completionDate || ''} onChange={e => handleInputChange('completionDate', e.target.value)} className="project-form-input" />
              </div>
              <div className="project-form-group">
                <label>Client</label>
                <input type="text" value={editedProject.client || ''} onChange={e => handleInputChange('client', e.target.value)} className="project-form-input" />
              </div>
            </>
          ) : (
            <div className="project-form-group">
              <label>Expected Completion</label>
              <input type="text" value={editedProject.expectedCompletion || ''} onChange={e => handleInputChange('expectedCompletion', e.target.value)} placeholder="e.g., Q3 2024" className="project-form-input" />
            </div>
          )}
          {/* Photos */}
          <div className="project-form-group">
            <label>Project Photos ({totalCount}/6)</label>
            <div className="project-photos-management">
              <input type="file" multiple accept="image/*" onChange={handleFileChange} className="project-photo-input" disabled={!canAddMore} />
              <p>{canAddMore ? `Select up to ${6 - totalCount} more photos` : 'Maximum 6 photos reached'}</p>
              {newPhotos.length > 0 && (
                <div className="project-new-photos">
                  <h4>New Photos:</h4>
                  <div className="project-photos-grid">
                    {newPhotos.map((photo, index) => (
                      <div key={index} className="project-photo-item">
                        <img src={URL.createObjectURL(photo)} alt={`New ${index + 1}`} />
                        <button type="button" onClick={() => handleRemoveNewPhoto(index)} className="project-remove-photo-btn"><FaTimes /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {editedProject.photos && editedProject.photos.length > 0 && (
                <div className="project-existing-photos">
                  <h4>Existing Photos:</h4>
                  <div className="project-photos-grid">
                    {editedProject.photos.map((photo, index) => (
                      <div key={index} className="project-photo-item">
                        <img src={getImageUrl(photo)} alt={`Existing ${index + 1}`} />
                        <button type="button" onClick={() => { if (window.confirm('Delete this photo?')) onDeletePhoto(editedProject._id, index); }} className="project-remove-photo-btn"><FaTimes /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Completed checkbox */}
          <div className="project-form-group project-checkbox-group">
            <label className="project-checkbox-label">
              <input
                type="checkbox"
                checked={!!editedProject.completed}
                onChange={e => handleCheckboxChange('completed', e.target.checked)}
                className="project-checkbox-input"
              />
              <span className="project-checkmark"></span>
              Mark as Completed
            </label>
          </div>
          {/* Actions */}
          <div className="project-editor-actions">
            <button type="submit" className="admin-btn admin-btn-primary" disabled={uploading}>
              {uploading ? (
                <>
                  <FaSpinner className="spinner" /> Saving...
                </>
              ) : 'Save Project'}
            </button>
            <button type="button" onClick={onCancel} className="admin-btn admin-btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminInterface;